import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { listUsers } from '@/api/users'
import { listCompanies } from '@/api/companies'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { formatPhoneDisplay } from '@/utils/phone'
import { toDateTimeLocalInput } from '@/utils/format'

/**
 * Модалка ручного выбора инспектора. Возвращает выбранного через onPick.
 * Список — все активные инспекторы; есть поиск по ФИО/телефону/компании.
 * Необязательное «Время осмотра» — если уже договорились с клиентом (ISO или null).
 */
export function InspectorPicker({
  open,
  onClose,
  onPick,
  isPending,
  inspectionAt = null,
}: {
  open: boolean
  onClose: () => void
  onPick: (inspectorId: string, inspectionAt: string | null) => void
  isPending?: boolean
  inspectionAt?: string | null
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<string | null>(null)
  const [when, setWhen] = useState(inspectionAt ? toDateTimeLocalInput(inspectionAt) : '')

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['inspectors', 'all'],
    queryFn: () =>
      listUsers({ role: 'inspector', includeInactive: false, pageSize: 200 }),
    enabled: open,
  })
  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'active'],
    queryFn: () => listCompanies({ pageSize: 200, type: 'all' }),
    enabled: open,
  })
  const companyById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of companiesData?.items ?? []) m.set(c.id, c.name)
    return m
  }, [companiesData])

  const items = usersData?.items ?? []
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    const qDigits = q.replace(/\D/g, '')
    return items.filter((u) => {
      if (u.full_name.toLowerCase().includes(q)) return true
      if (qDigits && u.phone.replace(/\D/g, '').includes(qDigits)) return true
      const cname = u.company_id ? companyById.get(u.company_id) : ''
      if (cname && cname.toLowerCase().includes(q)) return true
      return false
    })
  }, [items, search, companyById])

  if (!open) return null
  return (
    <Modal
      title={t('orderBlocks.inspectorPicker.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('orderBlocks.inspectorPicker.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!picked || isPending}
            onClick={() => picked && onPick(picked, when ? new Date(when).toISOString() : null)}
          >
            {isPending ? t('orderBlocks.inspectorPicker.assigning') : t('orderBlocks.inspectorPicker.assign')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="flex flex-col gap-1">
          <span className="stryzh-input-label">{t('orderBlocks.inspectorPicker.inspectionAt')}</span>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="min-h-9 rounded-[var(--stryzh-radius-sm)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-2 text-[color:var(--stryzh-color-text-primary)] [color-scheme:dark]"
          />
        </label>
        <Input
          placeholder={t('orderBlocks.inspectorPicker.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="max-h-[55vh] overflow-y-auto rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)]">
          {isLoading ? (
            <p className="px-3 py-4 text-sm text-[color:var(--stryzh-color-text-secondary)]">
              {t('orderBlocks.inspectorPicker.loading')}
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-4 text-sm text-[color:var(--stryzh-color-text-secondary)]">
              {t('orderBlocks.inspectorPicker.empty')}
            </p>
          ) : (
            <ul>
              {filtered.map((u) => {
                const sel = picked === u.id
                const cname = u.company_id ? companyById.get(u.company_id) : null
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(u.id)}
                      className={
                        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition ' +
                        (sel
                          ? 'bg-[color:var(--stryzh-color-accent-orange)]/15'
                          : 'hover:bg-[color:var(--stryzh-color-fill-input)]')
                      }
                    >
                      <span className="min-w-0 truncate">
                        <span className="text-[color:var(--stryzh-color-text-primary)]">
                          {u.full_name || '—'}
                        </span>
                        {cname ? (
                          <span className="ml-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                            · {cname}
                          </span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                        {formatPhoneDisplay(u.phone)}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  )
}
